<template>
  <div class="task-hierarchy">
    <!-- Tree View -->
    <div v-if="loading" class="hierarchy-loading">
      {{ __('Loading…') }}
    </div>
    <div v-else-if="treeError" class="hierarchy-error">
      {{ treeError }}
    </div>
    <div v-else-if="treeData" class="hierarchy-section tree-section">
      <TaskTreeNode
        :task="treeData"
        :project-id="projectId"
        :chat-id="chatId"
        :from-id="fromId"
        :depth="0"
        :is-root="true"
        :can-manage-relations="canManageRelations"
        :current-task-id="currentTaskGlobalId"
        :current-task-parent-id="currentTaskParentId"
        :current-task-child-ids="currentTaskChildIds"
        :unlinking-parent="removing"
        :unlinking-child-id="unlinkingChildId"
        @unlink-parent="removeParent"
        @unlink-child="unlinkChild"
      />
    </div>
    <div v-else-if="!hasHierarchy && !canManageRelations" class="empty-state">
      {{ s__('TaskHierarchy|No hierarchy defined') }}
    </div>

    <!-- Hierarchy Management Actions -->
    <div
      v-if="canManageRelations && (canHaveChildren || canSetParent)"
      class="hierarchy-section add-section hierarchy-actions"
    >
      <!-- Action Buttons (show when forms are closed) -->
      <div
        v-if="!showChildTaskForm && !showChildSelector && !showParentSelector"
        class="action-buttons"
      >
        <button
          v-if="canHaveChildren"
          class="action-btn create-btn add-child-btn"
          @click="showChildTaskForm = true"
        >
          + {{ s__('TaskHierarchy|Create Child') }}
        </button>
        <button
          v-if="canHaveChildren"
          class="action-btn link-btn"
          @click="showChildSelector = true"
        >
          🔗 {{ s__('TaskHierarchy|Link Child') }}
        </button>
        <button
          v-if="canSetParent"
          class="action-btn parent-btn add-parent-btn"
          @click="showParentSelector = true"
        >
          {{
            hasCurrentParent ? s__('TaskHierarchy|Change Parent') : s__('TaskHierarchy|Set Parent')
          }}
        </button>
      </div>

      <!-- Create Child Task Form -->
      <div v-if="showChildTaskForm" class="action-form">
        <div class="form-header">
          <strong>{{ s__('TaskHierarchy|Create Child Task') }}</strong>
        </div>
        <div class="form-row">
          <label>{{ s__('TaskHierarchy|Title') }} *</label>
          <SingleLineTextarea
            v-model="childTaskForm.title"
            class="child-task-title-input"
            :placeholder="s__('TaskHierarchy|Enter task title')"
          />
        </div>
        <div v-if="projectTaskCategories && projectTaskCategories.length > 1" class="form-row">
          <label>{{ s__('TaskHierarchy|Type') }}</label>
          <select v-model="childTaskForm.taskCategoryId">
            <option
              v-for="category in projectTaskCategories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
        </div>
        <div v-if="projectMembers && projectMembers.length > 0" class="form-row">
          <label>{{ s__('TaskHierarchy|Assignee') }}</label>
          <UserSelect
            v-model="childTaskForm.assigneeId"
            :users="projectMembers"
            :placeholder="s__('TaskHierarchy|Unassigned')"
            :searchable="true"
            :project-id="projectId"
          />
        </div>
        <div v-if="isPriorityRequired" class="form-row">
          <label>{{ __('Priority') }}<span> *</span></label>
          <select v-model="childTaskForm.priority">
            <option :value="null">{{ s__('TaskForm|None') }}</option>
            <option v-for="p in sortedPriorities" :key="p.value" :value="p.value">
              {{ translatePriority(p.value) }}
            </option>
          </select>
        </div>
        <div class="form-row">
          <label>{{ s__('TaskHierarchy|Due') }}<span v-if="isDueAtRequired"> *</span></label>
          <DateTimeInput
            :datetime="childTaskForm.dueAt"
            :title="s__('TaskHierarchy|Due')"
            @update:datetime="childTaskForm.dueAt = $event"
          />
        </div>
        <div class="form-actions">
          <button
            :disabled="!canCreateChildTask || creatingChild"
            class="btn-sm btn-primary"
            @click="createChildTask"
          >
            {{ creatingChild ? s__('TaskHierarchy|Creating...') : s__('TaskHierarchy|Create') }}
          </button>
          <button class="btn-sm btn-secondary" @click="cancelChildTaskForm">
            {{ __('Cancel') }}
          </button>
        </div>
        <div class="form-actions" style="margin-top: 8px">
          <a :href="newTaskUrl" class="link">{{ s__('TaskHierarchy|Extended form') }}</a>
        </div>
        <div v-if="childTaskError" class="error-message">{{ childTaskError }}</div>
      </div>

      <!-- Link Existing Task as Child Form -->
      <div v-if="showChildSelector" class="action-form">
        <div class="form-header">
          <strong>{{ s__('TaskHierarchy|Link Existing Task as Child') }}</strong>
        </div>
        <div class="form-row">
          <TaskSelect
            v-model="selectedChildId"
            :project-id="projectId"
            :placeholder="s__('TaskHierarchy|Select task...')"
            :search-placeholder="s__('TaskHierarchy|Search tasks...')"
            :exclude-task-ids="[String(taskId)]"
          />
        </div>
        <div class="form-actions">
          <button
            :disabled="!selectedChildId || linkingChild"
            class="btn-sm"
            @click="confirmLinkChild"
          >
            {{ linkingChild ? s__('TaskHierarchy|Linking...') : s__('TaskHierarchy|Link') }}
          </button>
          <button class="btn-sm btn-secondary" @click="cancelChildSelection">
            {{ __('Cancel') }}
          </button>
        </div>
      </div>

      <!-- Set Parent Form -->
      <div v-if="showParentSelector" class="action-form">
        <div class="form-header">
          <strong>{{
            hasCurrentParent
              ? s__('TaskHierarchy|Change Parent')
              : s__('TaskHierarchy|Set Parent Task')
          }}</strong>
        </div>
        <div class="form-row">
          <TaskSelect
            v-model="selectedParentId"
            :project-id="projectId"
            :placeholder="s__('TaskHierarchy|Select task...')"
            :search-placeholder="s__('TaskHierarchy|Search tasks...')"
            :exclude-task-ids="[String(taskId)]"
          />
        </div>
        <div class="form-actions">
          <button
            :disabled="!selectedParentId || settingParent"
            class="btn-sm"
            @click="confirmSelectParent"
          >
            {{ settingParent ? s__('TaskHierarchy|Setting...') : s__('TaskHierarchy|Set') }}
          </button>
          <button class="btn-sm btn-secondary" @click="cancelSelection">{{ __('Cancel') }}</button>
        </div>
      </div>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useMutation, useLazyQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__, __, translatePriority } from '~/locale'
import TaskSelect from '@/components/common/TaskSelect.vue'
import UserSelect from '@/components/common/UserSelect.vue'
import DateTimeInput from '@/components/common/DateTimeInput.vue'
import SingleLineTextarea from '@/components/common/SingleLineTextarea.vue'
import TaskTreeNode from './TaskTreeNode.vue'

const props = defineProps({
  taskId: { type: [String, Number], required: true },
  taskGlobalId: { type: [String, Number], default: null },
  projectId: { type: [String, Number], required: true },
  canManageRelations: { type: Boolean, default: false },
  chatId: { type: [String, Number], default: null },
  fromId: { type: [String, Number], default: null },
  taskCategoryId: { type: [String, Number], default: null },
  projectMembers: { type: Array, default: () => [] },
  projectTaskCategories: { type: Array, default: () => [] },
  priorities: { type: Array, default: () => [] },
  requiredFields: { type: Array, default: () => [] },
  isPersonalProject: { type: Boolean, default: false },
  expanded: { type: Boolean, default: false },
})

const currentTaskGlobalId = computed(() => String(props.taskGlobalId || props.taskId))

// Check if there's any hierarchy (parent or children)
// We check treeData instead of props since we fetch the full tree ourselves
const hasHierarchy = computed(() => {
  if (!treeData.value) return false

  const currentTaskGlobalId = String(props.taskGlobalId || treeData.value.id || '')
  // Check if current task has a parent (root is not current task)
  const hasParent = treeData.value.id !== currentTaskGlobalId

  // Check if current task has children
  function hasChildren(node, targetId) {
    if (String(node.id) === String(targetId)) {
      return node.children && node.children.length > 0
    }
    if (node.children) {
      for (const child of node.children) {
        if (hasChildren(child, targetId)) return true
      }
    }
    return false
  }

  const hasChildrenInTree = hasChildren(treeData.value, currentTaskGlobalId)

  return hasParent || hasChildrenInTree
})

// GraphQL query to fetch full hierarchy tree
const TASK_HIERARCHY_QUERY = gql`
  query GetTaskHierarchy($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        iid
        title
        priority
        dueAt
        workflowStatus {
          id
          name
          color
          isFinal
        }
        assignee {
          id
          name
          username
        }
        parentTask {
          id
          iid
          title
          priority
          dueAt
          workflowStatus {
            id
            name
            color
            isFinal
          }
          assignee {
            id
            name
            username
          }
          parentTask {
            id
            iid
            title
            priority
            dueAt
            workflowStatus {
              id
              name
              color
              isFinal
            }
            assignee {
              id
              name
              username
            }
            parentTask {
              id
              iid
              title
              priority
              dueAt
              workflowStatus {
                id
                name
                color
                isFinal
              }
              assignee {
                id
                name
                username
              }
            }
            childTasks {
              id
              iid
              title
              priority
              dueAt
              workflowStatus {
                id
                name
                color
                isFinal
              }
              assignee {
                id
                name
                username
              }
              childTasks {
                id
                iid
                title
                priority
                dueAt
                workflowStatus {
                  id
                  name
                  color
                  isFinal
                }
                assignee {
                  id
                  name
                  username
                }
                childTasks {
                  id
                  iid
                  title
                  priority
                  dueAt
                  workflowStatus {
                    id
                    name
                    color
                    isFinal
                  }
                  assignee {
                    id
                    name
                    username
                  }
                }
              }
            }
          }
          childTasks {
            id
            iid
            title
            priority
            dueAt
            workflowStatus {
              id
              name
              color
              isFinal
            }
            assignee {
              id
              name
              username
            }
            childTasks {
              id
              iid
              title
              priority
              dueAt
              workflowStatus {
                id
                name
                color
                isFinal
              }
              assignee {
                id
                name
                username
              }
              childTasks {
                id
                iid
                title
                priority
                dueAt
                workflowStatus {
                  id
                  name
                  color
                  isFinal
                }
                assignee {
                  id
                  name
                  username
                }
              }
            }
          }
        }
        childTasks {
          id
          iid
          title
          priority
          dueAt
          workflowStatus {
            id
            name
            color
            isFinal
          }
          assignee {
            id
            name
            username
          }
          childTasks {
            id
            iid
            title
            priority
            dueAt
            workflowStatus {
              id
              name
              color
              isFinal
            }
            assignee {
              id
              name
              username
            }
            childTasks {
              id
              iid
              title
              priority
              dueAt
              workflowStatus {
                id
                name
                color
                isFinal
              }
              assignee {
                id
                name
                username
              }
            }
          }
        }
      }
    }
  }
`

const {
  load: loadHierarchy,
  result: hierarchyResult,
  loading,
  error: queryError,
} = useLazyQuery(
  TASK_HIERARCHY_QUERY,
  () => ({
    projectId: String(props.projectId),
    taskId: currentTaskGlobalId.value,
  }),
  {
    fetchPolicy: 'no-cache', // Completely bypass cache to avoid triggering main query refetch
  }
)

const treeError = computed(() => {
  if (queryError.value) {
    return queryError.value.message || String(queryError.value)
  }
  return null
})

const treeData = computed(() => {
  const task = hierarchyResult.value?.project?.task
  if (!task) return null

  // Build a complete map of all tasks from the query result
  const taskMap = new Map()

  // Helper to recursively add a task and all its relations to the map
  function addTaskToMap(t, depth = 0) {
    if (!t || depth > 10) return // Prevent infinite recursion

    const id = String(t.id)

    // If we don't have this task yet, add it
    if (!taskMap.has(id)) {
      taskMap.set(id, {
        id: id,
        iid: t.iid,
        title: t.title,
        priority: t.priority,
        dueAt: t.dueAt,
        workflowStatus: t.workflowStatus,
        assignee: t.assignee,
        childIds: new Set(),
        parentId: null,
      })
    }

    const taskData = taskMap.get(id)

    // Process parent first (to ensure parent exists before we try to update it)
    if (t.parentTask) {
      const parentId = String(t.parentTask.id)
      taskData.parentId = parentId

      // Recursively add parent FIRST
      addTaskToMap(t.parentTask, depth + 1)

      // Now that parent is definitely in map, add this task as a child of its parent
      const parentData = taskMap.get(parentId)
      if (parentData) {
        parentData.childIds.add(id)
      }
    }

    // Process children
    if (t.childTasks && Array.isArray(t.childTasks)) {
      t.childTasks.forEach(child => {
        if (child) {
          const childId = String(child.id)
          taskData.childIds.add(childId)

          // Recursively add child
          addTaskToMap(child, depth + 1)

          // Make sure the child knows about this parent
          const childData = taskMap.get(childId)
          if (childData && !childData.parentId) {
            childData.parentId = id
          }
        }
      })
    }
  }

  // Start by adding the queried task and all its relations
  addTaskToMap(task)

  // Find the root by traversing up from the current task
  let rootId = String(task.id)
  let visited = new Set()
  let currentId = rootId

  while (taskMap.has(currentId) && taskMap.get(currentId).parentId) {
    const parentId = taskMap.get(currentId).parentId

    // Check for cycles
    if (visited.has(parentId)) break
    visited.add(currentId)

    rootId = parentId
    currentId = parentId
  }

  // Build tree recursively from root
  function buildTree(nodeId) {
    if (!taskMap.has(nodeId)) return null

    const node = taskMap.get(nodeId)
    const children = Array.from(node.childIds)
      .map(childId => buildTree(childId))
      .filter(Boolean)

    return {
      id: node.id,
      iid: node.iid,
      title: node.title,
      priority: node.priority,
      dueAt: node.dueAt,
      workflowStatus: node.workflowStatus,
      assignee: node.assignee,
      parentId: node.parentId,
      children: children,
      isCurrentTask: node.id === currentTaskGlobalId.value,
    }
  }

  return buildTree(rootId)
})

// Load hierarchy on mount if expanded is true (component is only mounted when expanded)
onMounted(() => {
  if (props.expanded) {
    loadHierarchy()
  }
})

// Load hierarchy when expanded changes (though this rarely happens since component is v-if controlled)
watch(
  () => props.expanded,
  newVal => {
    if (newVal) {
      loadHierarchy()
    }
  }
)

// Also watch taskId changes to reload if already expanded
watch(
  () => [props.taskId, props.taskGlobalId],
  () => {
    if (props.expanded) {
      loadHierarchy()
    }
  }
)

// Sort priorities so higher priorities appear first
const sortedPriorities = computed(() => {
  return [...props.priorities].reverse()
})

// Check if priority is required
const isPriorityRequired = computed(() => {
  return !props.isPersonalProject && props.requiredFields.includes('priority')
})

const isDueAtRequired = computed(() => {
  return !props.isPersonalProject && props.requiredFields.includes('due_at')
})

const emit = defineEmits(['parent-changed', 'child-created'])

const showParentSelector = ref(false)
const selectedParentId = ref('')
const showChildSelector = ref(false)
const selectedChildId = ref('')
const removing = ref(false)
const unlinkingChildId = ref(null)
const settingParent = ref(false)
const linkingChild = ref(false)
const error = ref(null)

const showChildTaskForm = ref(false)
const creatingChild = ref(false)
const childTaskError = ref(null)
const childTaskForm = ref({
  title: '',
  taskCategoryId: null,
  assigneeId: null,
  priority: null,
  dueAt: null,
})

// Task can have a parent if it's not already at max depth and moving it wouldn't exceed max depth
// We need to check the actual depth in the tree
const canSetParent = computed(() => {
  if (!treeData.value) return true

  // Find current task in tree and calculate its depth
  function findTaskDepth(node, targetId, depth = 0) {
    if (String(node.id) === String(targetId)) return depth
    if (node.children) {
      for (const child of node.children) {
        const found = findTaskDepth(child, targetId, depth + 1)
        if (found !== null) return found
      }
    }
    return null
  }

  const currentDepth = findTaskDepth(treeData.value, currentTaskGlobalId.value)
  if (currentDepth === null) return true

  // Check if current task has children in the tree
  function hasChildren(node, targetId) {
    if (String(node.id) === String(targetId)) {
      return node.children && node.children.length > 0
    }
    if (node.children) {
      for (const child of node.children) {
        if (hasChildren(child, targetId)) return true
      }
    }
    return false
  }

  const hasChildrenInTree = hasChildren(treeData.value, currentTaskGlobalId.value)

  // If task has children, it needs room for them in the new hierarchy
  // Max depth is 3, so if we have children at relative depth 2, we can only be at depth 1
  if (hasChildrenInTree) {
    return currentDepth < 2
  }
  return true
})

// Task can have children if it's not already at max depth (depth 3)
const canHaveChildren = computed(() => {
  if (!treeData.value) return true

  // Find current task in tree and calculate its depth
  function findTaskDepth(node, targetId, depth = 0) {
    if (String(node.id) === String(targetId)) return depth
    if (node.children) {
      for (const child of node.children) {
        const found = findTaskDepth(child, targetId, depth + 1)
        if (found !== null) return found
      }
    }
    return null
  }

  const currentDepth = findTaskDepth(treeData.value, currentTaskGlobalId.value)
  if (currentDepth === null) return true

  return currentDepth < 3
})

const canCreateChildTask = computed(() => {
  if (childTaskForm.value.title.trim().length === 0) return false
  if (isPriorityRequired.value && !childTaskForm.value.priority) return false
  if (isDueAtRequired.value && !childTaskForm.value.dueAt) return false
  return true
})

const currentTaskNode = computed(() => {
  if (!treeData.value) return null

  function findNode(node) {
    if (!node) return null
    if (String(node.id) === currentTaskGlobalId.value) return node

    if (!node.children || node.children.length === 0) return null

    for (const child of node.children) {
      const found = findNode(child)
      if (found) return found
    }

    return null
  }

  return findNode(treeData.value)
})

const currentTaskParentId = computed(() => currentTaskNode.value?.parentId || null)
const currentTaskChildIds = computed(() => {
  const children = currentTaskNode.value?.children || []
  return children.map(child => String(child.id))
})
const hasCurrentParent = computed(() => Boolean(currentTaskParentId.value))

const UPDATE_PARENT_MUTATION = gql`
  mutation UpdateTaskParent($taskId: ID!, $parentTaskId: ID) {
    updateTaskParent(taskId: $taskId, parentTaskId: $parentTaskId) {
      task {
        id
        parentTask {
          id
          title
        }
        childTasks {
          id
          title
        }
        depthLevel
      }
      errors
    }
  }
`

const CREATE_TASK_MUTATION = gql`
  mutation CreateTask(
    $projectId: ID!
    $taskCategoryId: ID!
    $title: String!
    $assigneeId: ID
    $priority: PriorityEnum
    $dueAt: ISO8601DateTime
    $workflowStatusId: ID
  ) {
    createTask(
      projectId: $projectId
      taskCategoryId: $taskCategoryId
      title: $title
      assigneeId: $assigneeId
      priority: $priority
      dueAt: $dueAt
      workflowStatusId: $workflowStatusId
    ) {
      task {
        id
        title
      }
      errors
    }
  }
`

const { mutate: updateParent } = useMutation(UPDATE_PARENT_MUTATION)
const { mutate: createTask } = useMutation(CREATE_TASK_MUTATION)

function taskUrl(taskId) {
  let url = `/projects/${props.projectId}/tasks/${taskId}`
  const params = []
  if (props.chatId) params.push(`chat_id=${props.chatId}`)
  if (props.fromId) params.push(`from_id=${props.fromId}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

const newTaskUrl = computed(() => {
  let url = `/projects/${props.projectId}/tasks/new`
  const params = []
  const currentTaskGlobalId = props.taskGlobalId || treeData.value?.id || null

  // Add parent task ID
  if (currentTaskGlobalId) {
    params.push(`parent_task_id=${currentTaskGlobalId}`)
  }

  // Add pre-filled form values
  if (childTaskForm.value.title) {
    params.push(`title=${encodeURIComponent(childTaskForm.value.title)}`)
  }
  if (childTaskForm.value.assigneeId) {
    params.push(`assignee_id=${childTaskForm.value.assigneeId}`)
  }
  if (childTaskForm.value.priority) {
    params.push(`priority=${childTaskForm.value.priority}`)
  }
  if (childTaskForm.value.dueAt) {
    params.push(`due_at=${encodeURIComponent(childTaskForm.value.dueAt)}`)
  }
  if (childTaskForm.value.taskCategoryId) {
    params.push(`task_category_id=${childTaskForm.value.taskCategoryId}`)
  }

  // Preserve chat_id and from_id
  if (props.chatId) params.push(`chat_id=${props.chatId}`)
  if (props.fromId) params.push(`from_id=${props.fromId}`)

  if (params.length > 0) url += '?' + params.join('&')
  return url
})

async function confirmSelectParent() {
  if (!selectedParentId.value) return

  const currentTaskGlobalId = props.taskGlobalId || treeData.value?.id
  if (!currentTaskGlobalId) return

  settingParent.value = true
  error.value = null

  try {
    const result = await updateParent({
      taskId: currentTaskGlobalId,
      parentTaskId: selectedParentId.value,
    })

    if (result.data?.updateTaskParent?.errors?.length > 0) {
      error.value = result.data.updateTaskParent.errors.join(', ')
    } else {
      showParentSelector.value = false
      selectedParentId.value = ''
      emit('parent-changed')
      loadHierarchy() // Reload tree after parent change
    }
  } catch (err) {
    error.value = err.message
  } finally {
    settingParent.value = false
  }
}

async function removeParent() {
  if (!hasCurrentParent.value || removing.value) return
  if (!confirm(s__('TaskHierarchy|Remove parent task?'))) return

  const currentTaskGlobalId = props.taskGlobalId || treeData.value?.id
  if (!currentTaskGlobalId) return

  removing.value = true
  error.value = null

  try {
    const result = await updateParent({
      taskId: currentTaskGlobalId,
      parentTaskId: null,
    })

    if (result.data?.updateTaskParent?.errors?.length > 0) {
      error.value = result.data.updateTaskParent.errors.join(', ')
    } else {
      showParentSelector.value = false
      selectedParentId.value = ''
      emit('parent-changed')
      loadHierarchy() // Reload tree after parent change
    }
  } catch (err) {
    error.value = err.message
  } finally {
    removing.value = false
  }
}

async function unlinkChild(childTaskId) {
  if (!childTaskId || unlinkingChildId.value) return
  if (!confirm(s__('TaskHierarchy|Unlink child task?'))) return

  unlinkingChildId.value = String(childTaskId)
  error.value = null

  try {
    const result = await updateParent({
      taskId: childTaskId,
      parentTaskId: null,
    })

    if (result.data?.updateTaskParent?.errors?.length > 0) {
      error.value = result.data.updateTaskParent.errors.join(', ')
    } else {
      emit('parent-changed')
      loadHierarchy()
    }
  } catch (err) {
    error.value = err.message
  } finally {
    unlinkingChildId.value = null
  }
}

function cancelSelection() {
  showParentSelector.value = false
  selectedParentId.value = ''
}

function cancelChildSelection() {
  showChildSelector.value = false
  selectedChildId.value = ''
}

async function confirmLinkChild() {
  if (!selectedChildId.value) return

  const currentTaskGlobalId = props.taskGlobalId || treeData.value?.id
  if (!currentTaskGlobalId) return

  linkingChild.value = true
  error.value = null

  try {
    const result = await updateParent({
      taskId: selectedChildId.value,
      parentTaskId: currentTaskGlobalId,
    })

    if (result.data?.updateTaskParent?.errors?.length > 0) {
      error.value = result.data.updateTaskParent.errors.join(', ')
    } else {
      showChildSelector.value = false
      selectedChildId.value = ''
      emit('child-created')
      loadHierarchy() // Reload tree after child link
    }
  } catch (err) {
    error.value = err.message
  } finally {
    linkingChild.value = false
  }
}

// Reset selection when selector opens
watch(showParentSelector, val => {
  if (val) {
    selectedParentId.value = ''
  }
})

// Reset child selection when selector opens
watch(showChildSelector, val => {
  if (val) {
    selectedChildId.value = ''
  }
})

// Reset child task form when it opens
watch(showChildTaskForm, val => {
  if (val) {
    // Use default task category (first one) or current task's category
    const defaultCategoryId =
      props.projectTaskCategories && props.projectTaskCategories.length > 0
        ? props.projectTaskCategories[0].id
        : props.taskCategoryId || null

    childTaskForm.value = {
      title: '',
      taskCategoryId: defaultCategoryId,
      assigneeId: null,
      dueAt: null,
    }
    childTaskError.value = null
  }
})

async function createChildTask() {
  if (!canCreateChildTask.value) return
  if (isDueAtRequired.value && !childTaskForm.value.dueAt) {
    childTaskError.value = s__('TaskForm|Due is required')
    return
  }

  // Use selected category or default to first category or current task's category
  // Backend will use project's default if not provided
  const taskCategoryId =
    childTaskForm.value.taskCategoryId ||
    (props.projectTaskCategories && props.projectTaskCategories.length > 0
      ? props.projectTaskCategories[0].id
      : null) ||
    props.taskCategoryId

  creatingChild.value = true
  childTaskError.value = null

  try {
    // First, create the task
    // Backend will set default task category and initial workflow status if not provided
    const createVars = {
      projectId: props.projectId,
      taskCategoryId: taskCategoryId || undefined,
      title: childTaskForm.value.title.trim(),
      assigneeId: childTaskForm.value.assigneeId || undefined,
      priority: childTaskForm.value.priority || undefined,
      dueAt: childTaskForm.value.dueAt || undefined,
    }

    const createResult = await createTask(createVars)

    if (createResult.data?.createTask?.errors?.length > 0) {
      childTaskError.value = createResult.data.createTask.errors.join(', ')
      return
    }

    const newTaskId = createResult.data?.createTask?.task?.id
    if (!newTaskId) {
      childTaskError.value = s__('TaskHierarchy|Failed to create task')
      return
    }

    // Then, set this task as the parent of the newly created task
    const updateResult = await updateParent({
      taskId: newTaskId,
      parentTaskId: currentTaskGlobalId.value,
    })

    if (updateResult.data?.updateTaskParent?.errors?.length > 0) {
      childTaskError.value = updateResult.data.updateTaskParent.errors.join(', ')
      return
    }

    // Success - close form and emit event to refresh
    showChildTaskForm.value = false
    emit('child-created')
    loadHierarchy() // Reload tree after child creation
  } catch (err) {
    childTaskError.value = err.message || s__('TaskHierarchy|Failed to create child task')
  } finally {
    creatingChild.value = false
  }
}

function cancelChildTaskForm() {
  showChildTaskForm.value = false
  const defaultCategoryId =
    props.projectTaskCategories && props.projectTaskCategories.length > 0
      ? props.projectTaskCategories[0].id
      : props.taskCategoryId || null
  childTaskForm.value = {
    title: '',
    taskCategoryId: defaultCategoryId,
    assigneeId: null,
    priority: null,
    dueAt: null,
  }
  childTaskError.value = null
}
</script>

<style scoped>
.task-hierarchy {
  font-size: 14px;
}

.hierarchy-loading,
.hierarchy-error {
  text-align: center;
  padding: 20px;
  color: #757575;
}

.hierarchy-error {
  color: #c62828;
}

.tree-section {
  margin-bottom: 16px;
}

.hierarchy-section {
  margin-bottom: 12px;
}

/* Hierarchy Actions Section */
.hierarchy-actions {
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.action-btn {
  background: none;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s ease-in-out;
  white-space: nowrap;
}

.action-btn:hover {
  border-color: #2aabee;
  color: #2aabee;
  background: rgba(42, 171, 238, 0.05);
}

.action-btn.create-btn {
  border-color: #2aabee;
  color: #2aabee;
}

.action-btn.create-btn:hover {
  background: #2aabee;
  color: #fff;
}

.action-btn.link-btn {
  border-style: dashed;
}

.action-btn.parent-btn {
  border-style: dashed;
}

/* Make buttons stack on very small screens */
@media (max-width: 680px) {
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}

.action-form {
  margin-top: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.form-header {
  margin-bottom: 12px;
  font-size: 13px;
  color: #333;
}

.add-parent-btn {
  background: none;
  border: 1px dashed #d1d5db;
  color: #6b7280;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s ease-in-out;
}

.add-parent-btn:hover {
  border-color: #2aabee;
  color: #2aabee;
  background: rgba(42, 171, 238, 0.05);
}

.add-parent-form,
.add-child-form {
  margin-top: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.form-row {
  margin-bottom: 12px;
}

.form-row label {
  display: block;
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
  font-weight: 500;
}

.form-row input[type='text'],
.form-row input[type='date'],
.form-row textarea,
.form-row select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  color: #333;
}

.child-task-title-input {
  line-height: 1.4;
  resize: vertical;
  min-height: 72px;
}

.add-child-btn {
  background: none;
  border: 1px dashed #2aabee;
  color: #2aabee;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s ease-in-out;
}

.add-child-btn:hover {
  background: #2aabee;
  color: #fff;
  border-color: #2aabee;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

/* Button styles inherit from forms.css */

.empty-state {
  color: #888;
  font-style: italic;
}

.error-message {
  color: #ff3b30;
  font-size: 13px;
  margin-top: 8px;
}

@media (max-width: 767px) {
  .form-row input[type='text'],
  .form-row input[type='date'],
  .form-row textarea,
  .form-row select {
    font-size: 16px;
  }
}
</style>
